import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import {
  DUPLICATE_STATUSES,
  GENDERS,
  ID_DOCUMENT_TYPES,
  type DuplicateStatus,
  type Gender,
  type IdDocumentType,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Empty,
  MatriculeTag,
  Modal,
  PageHeader,
  Pagination,
  SegmentedPills,
  Table,
  Textarea,
  useAuth,
  useToast,
  type BadgeVariant,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'

const KEY = ['admin', 'duplicates'] as const

interface Row {
  id: string
  rule: string
  ruleLabel: string
  status: DuplicateStatus
  a: string
  b: string
  detectedAt: string
  reviewedAt: string | null
  reviewer: string | null
}

interface Card {
  id: string
  userNumber: string | null
  lastName: string | null
  firstName: string | null
  email: string
  phone: string | null
  birthDate: string | null
  birthPlace: string | null
  gender: Gender | null
  idDocumentType: IdDocumentType | null
  idDocumentNumber: string | null
  city: string | null
  region: string | null
  accountStatus: string
  accountCreatedAt: string | null
}

interface Detail {
  id: string
  rule: string
  ruleLabel: string
  rules: string[]
  status: DuplicateStatus
  detectedAt: string
  reviewedAt: string | null
  reviewer: string | null
  reviewNote: string | null
  a: Card | null
  b: Card | null
}

interface Rule {
  code: string
  label: string
  description: string
  isActive: boolean
  updatedAt: string | null
  updatedBy: string | null
}

const STATUS_VARIANT: Record<DuplicateStatus, BadgeVariant> = {
  open: 'warning',
  confirmed: 'danger',
  dismissed: 'neutral',
}

/**
 * Doublons potentiels (cahier §4.5, TODO phase 8) : l'outil signale, l'agent
 * décide. Aucune suppression ni fusion automatique.
 */
export function DuplicatesPage() {
  const { session } = useAuth()
  const [status, setStatus] = useState<DuplicateStatus>('open')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string | null>(null)
  const list = useQuery({
    queryKey: [...KEY, status, page],
    queryFn: () =>
      session.api.get<
        Paginated<Row> & { meta: { byStatus: Partial<Record<DuplicateStatus, number>> } }
      >('/admin/duplicates', {
        query: { status, page },
      }),
    placeholderData: keepPreviousData,
  })
  const counts = list.data?.meta.byStatus ?? {}

  const columns: Column<Row>[] = [
    { key: 'rule', header: 'Règle', cell: (r) => r.ruleLabel },
    { key: 'a', header: 'Candidat 1', cell: (r) => r.a },
    { key: 'b', header: 'Candidat 2', cell: (r) => r.b },
    { key: 'date', header: 'Détecté le', cell: (r) => when(r.detectedAt) },
    {
      key: 'status',
      header: 'Statut',
      cell: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{DUPLICATE_STATUSES[r.status]}</Badge>,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Dossiers"
        title="Doublons potentiels"
        description="Comptes qui semblent correspondre à la même personne. Examinez chaque paire et motivez votre décision : rien n’est supprimé ni fusionné automatiquement."
      />
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        <Card>
          <div className="filter-row">
            <SegmentedPills
              label="Statut"
              value={status}
              onChange={(v) => {
                setStatus(v)
                setPage(1)
              }}
              options={(Object.keys(DUPLICATE_STATUSES) as DuplicateStatus[]).map((s) => ({
                value: s,
                label: `${DUPLICATE_STATUSES[s]} · ${counts[s] ?? 0}`,
              }))}
            />
          </div>
          {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
          <Table
            columns={columns}
            rows={list.data?.data ?? []}
            rowKey={(r) => r.id}
            loading={list.isPending}
            caption="Doublons potentiels"
            onRowClick={(r) => setSelected(r.id)}
            empty={
              <Empty icon={<Copy aria-hidden="true" />} title="Aucune paire dans cette catégorie" />
            }
          />
          {list.data && list.data.meta.total > list.data.meta.perPage && (
            <Pagination
              page={page}
              perPage={list.data.meta.perPage}
              total={list.data.meta.total}
              onPageChange={setPage}
            />
          )}
        </Card>
        <RulesCard />
      </div>
      {selected && <ReviewModal id={selected} onClose={() => setSelected(null)} />}
    </>
  )
}

const FIELDS: [keyof Card, string, (c: Card) => string | null][] = [
  ['lastName', 'Nom', (c) => c.lastName],
  ['firstName', 'Prénom(s)', (c) => c.firstName],
  [
    'birthDate',
    'Date de naissance',
    (c) => (c.birthDate ? c.birthDate.split('-').reverse().join('/') : null),
  ],
  ['birthPlace', 'Lieu de naissance', (c) => c.birthPlace],
  ['gender', 'Sexe', (c) => (c.gender ? GENDERS[c.gender] : null)],
  [
    'idDocumentType',
    'Pièce',
    (c) => (c.idDocumentType ? ID_DOCUMENT_TYPES[c.idDocumentType].label : null),
  ],
  ['idDocumentNumber', 'N° de pièce', (c) => c.idDocumentNumber],
  ['email', 'Courriel', (c) => c.email],
  ['phone', 'Téléphone', (c) => c.phone],
  ['city', 'Ville', (c) => [c.city, c.region].filter(Boolean).join(', ') || null],
  ['accountCreatedAt', 'Compte créé le', (c) => when(c.accountCreatedAt)],
]

const norm = (v: string | null) =>
  (v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

function ReviewModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const detail = useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => session.api.get<Detail>(`/admin/duplicates/${id}`),
  })
  const review = useMutation({
    mutationFn: (decision: 'confirmed' | 'dismissed') =>
      session.api.post(`/admin/duplicates/${id}/review`, { decision, note }),
    onSuccess: async () => {
      toast.success('Décision enregistrée')
      await queryClient.invalidateQueries({ queryKey: KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const d = detail.data
  const open = d?.status === 'open'

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Comparer les deux comptes"
      description={
        d
          ? `Règle : ${d.ruleLabel}${d.rules.length > 1 ? ` (+ ${d.rules.length - 1} autre règle)` : ''}`
          : undefined
      }
      footer={
        open ? (
          <Can perm="candidates.duplicates.review">
            <Button variant="secondary" onClick={onClose}>
              Plus tard
            </Button>
            <Button
              variant="secondary"
              loading={review.isPending && review.variables === 'dismissed'}
              disabled={note.trim().length < 10}
              onClick={() => review.mutate('dismissed')}
            >
              Faux positif
            </Button>
            <Button
              variant="danger"
              loading={review.isPending && review.variables === 'confirmed'}
              disabled={note.trim().length < 10}
              onClick={() => review.mutate('confirmed')}
            >
              Doublon confirmé
            </Button>
          </Can>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Fermer
          </Button>
        )
      }
    >
      {detail.isError && <Alert tone="danger">{errorMessage(detail.error)}</Alert>}
      {d?.a && d.b && (
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          <div className="tbl-wrap">
            <table className="tbl audit-diff">
              <thead>
                <tr>
                  <th scope="col">Information</th>
                  <th scope="col">Candidat 1</th>
                  <th scope="col">Candidat 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Matricule utilisateur</th>
                  {[d.a, d.b].map((c) => (
                    <td key={c.id}>
                      {c.userNumber ? (
                        <MatriculeTag kind="utilisateur" value={c.userNumber} />
                      ) : (
                        <span className="text-muted">Non visible avec votre profil</span>
                      )}
                    </td>
                  ))}
                </tr>
                {FIELDS.map(([key, label, show]) => {
                  const va = show(d.a!)
                  const vb = show(d.b!)
                  const same = Boolean(
                    va && vb && norm(va) === norm(vb) && key !== 'accountCreatedAt'
                  )
                  return (
                    <tr key={key} data-changed={same || undefined}>
                      <th scope="row">
                        {label} {same && <span className="sr-only">(identique)</span>}
                      </th>
                      <td>{va ?? <span className="text-muted">—</span>}</td>
                      <td>{vb ?? <span className="text-muted">—</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
            Les lignes surlignées sont identiques (accents et casse ignorés).
          </p>
          {open ? (
            <Textarea
              label="Motif de la décision"
              required
              rows={3}
              hint="10 caractères minimum. « Doublon confirmé » ne supprime rien : il signale la situation lors du contrôle de recevabilité."
              value={note}
              error={fieldErrors(review.error).note}
              onChange={(e) => setNote(e.target.value)}
            />
          ) : (
            <Alert
              tone={d.status === 'confirmed' ? 'warning' : 'info'}
              title={DUPLICATE_STATUSES[d.status]}
            >
              {d.reviewNote} — {d.reviewer}, {when(d.reviewedAt)}
            </Alert>
          )}
        </div>
      )}
    </Modal>
  )
}

function RulesCard() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const rules = useQuery({
    queryKey: [...KEY, 'rules'],
    queryFn: () => session.api.get<{ data: Rule[] }>('/admin/duplicate-rules').then((r) => r.data),
  })
  const toggle = useMutation({
    mutationFn: ({ code, isActive }: { code: string; isActive: boolean }) =>
      session.api.put<{ detected: number }>(`/admin/duplicate-rules/${code}`, { isActive }),
    onSuccess: async (result, { isActive }) => {
      toast.success(
        isActive
          ? `Règle activée : ${result.detected} nouvelle(s) paire(s) détectée(s)`
          : 'Règle désactivée'
      )
      await queryClient.invalidateQueries({ queryKey: KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  return (
    <Card>
      <CardHeader title="Règles de détection" />
      <CardBody>
        <ul className="diff-list">
          {rules.data?.map((rule) => (
            <li key={rule.code}>
              <Can
                perm="settings.manage"
                fallback={
                  <span>
                    <Badge variant={rule.isActive ? 'success' : 'neutral'}>
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </Badge>{' '}
                    <strong>{rule.label}</strong>{' '}
                    <span className="text-muted">— {rule.description}</span>
                  </span>
                }
              >
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={
                      toggle.isPending && toggle.variables.code === rule.code
                        ? toggle.variables.isActive
                        : rule.isActive
                    }
                    disabled={toggle.isPending}
                    onChange={(e) => toggle.mutate({ code: rule.code, isActive: e.target.checked })}
                  />
                  <span>
                    <strong>{rule.label}</strong>{' '}
                    <span className="text-muted">— {rule.description}</span>
                  </span>
                </label>
              </Can>
            </li>
          ))}
        </ul>
        <p
          className="text-muted"
          style={{ marginBlock: 'var(--space-4) 0', fontSize: 'var(--text-sm)' }}
        >
          Le téléphone et l’adresse électronique sont déjà uniques par compte. Activer une règle
          analyse aussitôt tous les profils existants.
        </p>
      </CardBody>
    </Card>
  )
}
